import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { shallow } from 'zustand/shallow';

import { usePaymentFlowRestoreStore } from '../stores/usePaymentFlowRestoreStore';

interface IUseRestoreStateProps<T> {
	restoreState?: T;
	onStateRestored: (state: T) => void;
}

export const useRestoreState = <T>({
	restoreState,
	onStateRestored,
}: IUseRestoreStateProps<T>) => {
	const { isRestoring, setIsRestoring, setRestoreData } =
		usePaymentFlowRestoreStore(
			useShallow((s) => ({
				isRestoring: s.isRestoring,
				setIsRestoring: s.setIsRestoring,
				setRestoreData: s.setRestoreData,
			}))
		);

	const prevRestoreStateRef = useRef<T>();

	useEffect(() => {
		/** Save restoration data to persist store */
		if (!isRestoring) {
			/** Only update if data actually changed (shallow comparison) */
			if (!shallow(prevRestoreStateRef.current, restoreState)) {
				prevRestoreStateRef.current = restoreState;
				setRestoreData(restoreState);
			}
		}
	}, [isRestoring, restoreState, setRestoreData]);

	useEffect(() => {
		if (isRestoring) {
			/** We cannot pass T to the zustand store, so we need to cast it back to T. */
			const _restoredState = usePaymentFlowRestoreStore.getState()
				.restoreState as T;

			onStateRestored(_restoredState);
			setIsRestoring(false);
		}
	}, [isRestoring, onStateRestored, setIsRestoring, setRestoreData]);
};
